import Component from '@glimmer/component';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Card from 'tio-reimagined/components/card';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq, gt } from 'tio-ui/utilities';
import {
  PhUsersFour,
  PhTrendUp,
  PhWarning,
  PhGraduationCap,
  PhStar,
  PhChartBar,
  PhUser,
} from 'ember-phosphor-icons';
import PipelineFlow from 'tio-reimagined/components/pipeline-flow';
import { hash, array } from '@ember/helper';

// Unified employee type with all optional properties for different pipeline stages
interface PipelineEmployee {
  name: string;
  role: string;
  potential: string;
  timeInStage: string;
  // Emerging Leaders properties
  nextStep?: string;
  // Developing Leaders properties
  currentProgram?: string;
  progress?: number;
  // Ready Now properties
  targetRole?: string;
  matchScore?: number;
}

interface PipelineStage {
  name: string;
  count: number;
  description: string;
  color: string;
  employees: PipelineEmployee[];
}

// Mock leadership pipeline data
const LEADERSHIP_DATA: {
  summary: {
    currentLeaders: number;
    emergingLeaders: number;
    developingLeaders: number;
    readyNow: number;
    projectedRetirements: number;
    pipelineRatio: number;
    riskLevel: string;
  };
  stages: PipelineStage[];
  upcomingVacancies: Array<{
    role: string;
    timeframe: string;
    readyCandidates: number;
    status: string;
  }>;
  competencies: Array<{
    name: string;
    avgScore: number;
    target: number;
    gap: number;
  }>;
  cohorts: Array<{
    name: string;
    participants: number;
    startDate: string;
    progress: number;
    status: string;
  }>;
} = {
  summary: {
    currentLeaders: 156,
    emergingLeaders: 45,
    developingLeaders: 28,
    readyNow: 12,
    projectedRetirements: 18,
    pipelineRatio: 0.67,
    riskLevel: 'medium',
  },
  stages: [
    {
      name: 'Emerging Leaders',
      count: 45,
      description: 'High-potential employees identified for leadership track',
      color: 'violet',
      employees: [
        {
          name: 'Sarah Chen',
          role: 'Senior Nurse',
          potential: 'High',
          timeInStage: '6 months',
          nextStep: 'Leadership Foundations course',
        },
        {
          name: 'Marcus Johnson',
          role: 'Clinical Specialist',
          potential: 'High',
          timeInStage: '3 months',
          nextStep: 'Mentor assignment',
        },
        {
          name: 'Lisa Park',
          role: 'Staff Nurse',
          potential: 'Very High',
          timeInStage: '1 month',
          nextStep: 'Development plan creation',
        },
      ],
    },
    {
      name: 'Developing Leaders',
      count: 28,
      description: 'Actively completing leadership development programs',
      color: 'teal',
      employees: [
        {
          name: 'James Wilson',
          role: 'Charge Nurse',
          potential: 'High',
          timeInStage: '12 months',
          currentProgram: 'Leadership Essentials',
          progress: 75,
        },
        {
          name: 'Amanda Torres',
          role: 'Senior Nurse',
          potential: 'High',
          timeInStage: '8 months',
          currentProgram: 'Healthcare Management',
          progress: 45,
        },
        {
          name: 'David Kim',
          role: 'Clinical Lead',
          potential: 'Very High',
          timeInStage: '4 months',
          currentProgram: 'Executive Foundations',
          progress: 30,
        },
      ],
    },
    {
      name: 'Ready Now',
      count: 12,
      description: 'Qualified and ready for leadership positions',
      color: 'green',
      employees: [
        {
          name: 'Emily Rodriguez',
          role: 'Charge Nurse',
          potential: 'Very High',
          timeInStage: '2 months',
          targetRole: 'Nurse Manager',
          matchScore: 95,
        },
        {
          name: 'Michael Brown',
          role: 'Clinical Supervisor',
          potential: 'High',
          timeInStage: '4 months',
          targetRole: 'Department Head',
          matchScore: 88,
        },
        {
          name: 'Jennifer Lee',
          role: 'Senior Charge Nurse',
          potential: 'High',
          timeInStage: '1 month',
          targetRole: 'Nurse Manager',
          matchScore: 92,
        },
      ],
    },
  ],
  upcomingVacancies: [
    { role: 'Nurse Manager - ICU', timeframe: '3 months', readyCandidates: 2, status: 'covered' },
    {
      role: 'Clinical Director - Surgery',
      timeframe: '6 months',
      readyCandidates: 1,
      status: 'at-risk',
    },
    {
      role: 'Nurse Manager - Emergency',
      timeframe: '12 months',
      readyCandidates: 3,
      status: 'covered',
    },
    {
      role: 'Department Head - Pediatrics',
      timeframe: '18 months',
      readyCandidates: 0,
      status: 'critical',
    },
  ],
  competencies: [
    { name: 'Strategic Thinking', avgScore: 72, target: 80, gap: 8 },
    { name: 'Team Leadership', avgScore: 78, target: 85, gap: 7 },
    { name: 'Change Management', avgScore: 65, target: 80, gap: 15 },
    { name: 'Financial Acumen', avgScore: 58, target: 75, gap: 17 },
    { name: 'Communication', avgScore: 82, target: 85, gap: 3 },
  ],
  cohorts: [
    {
      name: 'Emerging Leaders 2025',
      participants: 15,
      startDate: 'Jan 2025',
      progress: 65,
      status: 'active',
    },
    {
      name: 'Executive Development',
      participants: 8,
      startDate: 'Mar 2025',
      progress: 40,
      status: 'active',
    },
    {
      name: 'New Manager Bootcamp',
      participants: 22,
      startDate: 'Feb 2025',
      progress: 80,
      status: 'active',
    },
  ],
};

export default class CareerPathwaysAdminLeadership extends Component {
  get data() {
    return LEADERSHIP_DATA;
  }

  get pipelineHealthPercent() {
    return Math.round(this.data.summary.pipelineRatio * 100);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 pb-12">
      {{! Header }}
      <div class="flex justify-between items-center mb-6">
        <div>
          <Heading @level={{1}} class="app-page-title">
            Leadership Pipeline
          </Heading>
          <Text @variant="muted">Succession planning and leadership development tracking</Text>
        </div>
        <div class="flex gap-3">
          <a href="/reimagined/admin/pipeline-report">
            <Button @appearance="outlined" @intent="primary">
              <PhChartBar @size={{18}} class="mr-2" />
              Pipeline Report
            </Button>
          </a>
          <Button @intent="primary">
            <PhUsersFour @size={{18}} class="mr-2" />
            Add to Pipeline
          </Button>
        </div>
      </div>

      {{! Pipeline Summary }}
      <Card @padding="lg" class="mb-6">
        <div class="flex items-center justify-between mb-4">
          <Heading @level={{2}} class="text-lg font-semibold dark:text-white">Pipeline Health</Heading>
          {{#if (eq this.data.summary.riskLevel "low")}}
            <Chip @intent="success">Low Risk</Chip>
          {{else if (eq this.data.summary.riskLevel "medium")}}
            <Chip @intent="warning">Medium Risk</Chip>
          {{else}}
            <Chip @intent="danger">High Risk</Chip>
          {{/if}}
        </div>

        {{! Pipeline Flow Visualization }}
        <PipelineFlow
          @stages={{array
            (hash value=this.data.summary.emergingLeaders label="Emerging" color="violet")
            (hash value=this.data.summary.developingLeaders label="Developing" color="teal")
            (hash value=this.data.summary.readyNow label="Ready Now" color="green")
            (hash value=this.data.summary.currentLeaders label="Current Leaders" color="gray")
          }}
          @equalWidth={{true}}
          class="mb-6"
        />

        <div class="grid grid-cols-2 gap-4">
          <div
            class="p-4 tio-bg-warning-soft dark:bg-amber-900/30 border tio-border-warning dark:border-amber-700 rounded-lg"
          >
            <div class="tio-row-tight mb-2">
              <PhWarning @size={{20}} class="tio-fg-warning dark:text-amber-400" />
              <Text class="font-semibold tio-fg-warning-h dark:text-amber-200">Projected Retirements</Text>
            </div>
            <Text
              class="tio-fg-warning-h dark:text-amber-300"
            >{{this.data.summary.projectedRetirements}}
              leaders expected to retire in next 24 months</Text>
          </div>
          <div
            class="p-4 tio-bg-brand-wash dark:bg-violet-900/30 border tio-border-brand-soft dark:border-violet-700 rounded-lg"
          >
            <div class="tio-row-tight mb-2">
              <PhTrendUp @size={{20}} class="tio-fg-brand dark:text-violet-400" />
              <Text class="font-semibold tio-fg-brand dark:text-violet-200">Pipeline Ratio</Text>
            </div>
            <Text class="tio-fg-brand dark:text-violet-300">{{this.pipelineHealthPercent}}% coverage
              for projected needs</Text>
          </div>
        </div>
      </Card>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Pipeline Stages }}
        <div class="lg:col-span-2 space-y-6">
          {{#each this.data.stages as |stage|}}
            <Card @padding="none">
              <div class="flex items-center justify-between p-5 border-b tio-border-subtle">
                <div class="tio-row-tight">
                  <PhUsersFour @size={{20}} class="text-{{stage.color}}-600" />
                  <span class="font-medium tio-fg-primary">{{stage.name}}</span>
                  <Chip @intent="default" @size="sm">{{stage.count}}</Chip>
                </div>
              </div>
              <div class="p-5">
                <Text @variant="muted" class="mb-4">{{stage.description}}</Text>
                <div class="space-y-3">
                  {{#each stage.employees as |emp|}}
                    <div class="tio-row-loose p-3 tio-bg-sunken dark:bg-gray-900/50 rounded-lg">
                      <div
                        class="w-10 h-10 rounded-full tio-bg-brand-soft dark:bg-violet-900/50 flex items-center justify-center"
                      >
                        <PhUser @size={{20}} class="tio-fg-brand dark:text-violet-400" />
                      </div>
                      <div class="flex-1">
                        <div class="tio-row-tight">
                          <Text class="font-semibold tio-fg-primary">{{emp.name}}</Text>
                          {{#if (eq emp.potential "Very High")}}
                            <PhStar @size={{16}} class="tio-fg-warning" />
                          {{/if}}
                        </div>
                        <Text @variant="muted" @size="sm">{{emp.role}}
                          •
                          {{emp.timeInStage}}
                          in stage</Text>
                      </div>
                      <div class="text-right">
                        {{#if emp.currentProgram}}
                          <Text @size="sm" class="tio-fg-secondary">{{emp.currentProgram}}</Text>
                          <div class="tio-row-tight mt-1">
                            <div class="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div class="h-full tio-bg-brand-accent rounded-full w-1/2"></div>
                            </div>
                            <span class="text-xs tio-fg-muted">{{emp.progress}}%</span>
                          </div>
                        {{else if emp.targetRole}}
                          <div class="tio-row-tight">
                            <Text @size="sm" class="tio-fg-secondary">Target:
                              {{emp.targetRole}}</Text>
                            <Chip @intent="success" @size="sm">{{emp.matchScore}}% match</Chip>
                          </div>
                        {{else}}
                          <Text @size="sm" class="tio-fg-secondary">{{emp.nextStep}}</Text>
                        {{/if}}
                      </div>
                    </div>
                  {{/each}}
                </div>
                <div class="mt-4">
                  <Button @appearance="outlined" @size="sm">View All {{stage.count}}</Button>
                </div>
              </div>
            </Card>
          {{/each}}
        </div>

        {{! Sidebar }}
        <div class="space-y-6">
          {{! Upcoming Vacancies }}
          <Card @padding="none">
            <div class="tio-row-tight p-5 border-b tio-border-subtle">
              <PhWarning @size={{20}} class="tio-fg-warning" />
              <span class="font-medium tio-fg-primary">Upcoming Vacancies</span>
            </div>
            <div class="p-5">
              <div class="space-y-3">
                {{#each this.data.upcomingVacancies as |vacancy|}}
                  <div class="p-3 tio-bg-sunken dark:bg-gray-900/50 rounded-lg">
                    <div class="flex items-center justify-between mb-1">
                      <Text class="font-medium tio-fg-primary">{{vacancy.role}}</Text>
                      {{#if (eq vacancy.status "covered")}}
                        <Chip @intent="success" @size="sm">Covered</Chip>
                      {{else if (eq vacancy.status "at-risk")}}
                        <Chip @intent="warning" @size="sm">At Risk</Chip>
                      {{else}}
                        <Chip @intent="danger" @size="sm">Critical</Chip>
                      {{/if}}
                    </div>
                    <Text @variant="muted" @size="sm">
                      {{vacancy.timeframe}}
                      •
                      {{vacancy.readyCandidates}}
                      ready candidates
                    </Text>
                  </div>
                {{/each}}
              </div>
            </div>
          </Card>

          {{! Competency Gaps }}
          <Card @padding="none">
            <div class="tio-row-tight p-5 border-b tio-border-subtle">
              <PhChartBar @size={{20}} class="tio-fg-brand" />
              <span class="font-medium tio-fg-primary">Leadership Competencies</span>
            </div>
            <div class="p-5">
              <div class="space-y-4">
                {{#each this.data.competencies as |comp|}}
                  <div>
                    <div class="flex items-center justify-between mb-1">
                      <Text @size="sm" class="font-medium">{{comp.name}}</Text>
                      <Text
                        @size="sm"
                        class="tio-fg-secondary"
                      >{{comp.avgScore}}/{{comp.target}}</Text>
                    </div>
                    <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div class="h-full tio-bg-brand-accent rounded-full w-3/4"></div>
                    </div>
                    {{#if (gt comp.gap 10)}}
                      <Text @variant="muted" @size="sm" class="tio-fg-danger dark:text-red-400">Gap:
                        {{comp.gap}}
                        pts</Text>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            </div>
          </Card>

          {{! Active Cohorts }}
          <Card @padding="none">
            <div class="tio-row-tight p-5 border-b tio-border-subtle">
              <PhGraduationCap @size={{20}} class="text-teal-600" />
              <span class="font-medium tio-fg-primary">Active Cohorts</span>
            </div>
            <div class="p-5">
              <div class="space-y-3">
                {{#each this.data.cohorts as |cohort|}}
                  <div class="p-3 tio-bg-sunken dark:bg-gray-900/50 rounded-lg">
                    <div class="flex items-center justify-between mb-2">
                      <Text class="font-medium tio-fg-primary">{{cohort.name}}</Text>
                      <Text @variant="muted" @size="sm">{{cohort.participants}} participants</Text>
                    </div>
                    <div class="tio-row-tight">
                      <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div class="h-full bg-teal-500 rounded-full w-[65%]"></div>
                      </div>
                      <Text @size="sm" class="tio-fg-secondary">{{cohort.progress}}%</Text>
                    </div>
                  </div>
                {{/each}}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </VStack>
  </template>
}
